import { Component, OnInit, OnDestroy, signal, computed, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { PwaService, CustomNotificationOptions } from '../../services/pwa.service';

@Component({
  selector: 'jk-pwa-manager',
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatSnackBarModule,
    MatProgressBarModule,
    MatChipsModule,
    MatDialogModule
],
  template: `
    <mat-card class="pwa-manager-card">
      <mat-card-header>
        <mat-card-title>
          <mat-icon>settings_applications</mat-icon>
          PWA 管理
        </mat-card-title>
        <mat-card-subtitle>
          漸進式網頁應用程式功能管理
        </mat-card-subtitle>
      </mat-card-header>

      <mat-card-content>
        <!-- 網路狀態 -->
        <div class="status-section">
          <h3>網路狀態</h3>
          <mat-chip-set>
            <mat-chip [color]="isOnline() ? 'primary' : 'warn'">
              <mat-icon>{{ isOnline() ? 'wifi' : 'wifi_off' }}</mat-icon>
              {{ isOnline() ? '線上' : '離線' }}
            </mat-chip>
            @if (isSlowNetwork()) {
              <mat-chip color="warn">
                <mat-icon>network_check</mat-icon>
                慢速網路
              </mat-chip>
            }
          </mat-chip-set>
        </div>

        <!-- PWA 狀態 -->
        <div class="status-section">
          <h3>PWA 狀態</h3>
          <mat-chip-set>
            <mat-chip [color]="isPwaMode() ? 'primary' : 'basic'">
              <mat-icon>{{ isPwaMode() ? 'phone_android' : 'web' }}</mat-icon>
              {{ isPwaMode() ? 'PWA 模式' : '瀏覽器模式' }}
            </mat-chip>
            @if (updateAvailable()) {
              <mat-chip color="accent">
                <mat-icon>system_update</mat-icon>
                有更新可用
              </mat-chip>
            }
          </mat-chip-set>
        </div>

        <!-- 快取資訊 -->
        <div class="status-section">
          <h3>快取資訊</h3>
          <div class="cache-info">
            <p>快取大小: {{ formatBytes(cacheSize()) }}</p>
            @if (cacheStatus().length > 0) {
              <div class="cache-details">
                @for (cache of cacheStatus(); track cache.name) {
                  <div class="cache-item">
                    <span>{{ cache.name }}</span>
                    <span>{{ cache.size }} 項目</span>
                  </div>
                }
              </div>
            }
          </div>
        </div>

        <!-- 通知狀態 -->
        <div class="status-section">
          <h3>通知權限</h3>
          <mat-chip-set>
            <mat-chip [color]="getNotificationChipColor()">
              <mat-icon>{{ getNotificationIcon() }}</mat-icon>
              {{ getNotificationText() }}
            </mat-chip>
          </mat-chip-set>
        </div>
      </mat-card-content>

      <mat-card-actions>
        <!-- 安裝 PWA -->
        @if (installPromptAvailable()) {
          <button mat-raised-button color="primary" (click)="installPwa()">
            <mat-icon>get_app</mat-icon>
            安裝應用程式
          </button>
        }

        <!-- 更新應用程式 -->
        @if (updateAvailable()) {
          <button mat-raised-button color="accent" (click)="updateApp()">
            <mat-icon>system_update</mat-icon>
            更新應用程式
          </button>
        }

        <!-- 請求通知權限 -->
        @if (notificationPermission() === 'default') {
          <button mat-raised-button (click)="requestNotificationPermission()">
            <mat-icon>notifications</mat-icon>
            啟用通知
          </button>
        }

        <!-- 清除快取 -->
        <button mat-stroked-button (click)="clearCache()">
          <mat-icon>clear_all</mat-icon>
          清除快取
        </button>

        <!-- 測試通知 -->
        @if (notificationPermission() === 'granted') {
          <button mat-stroked-button (click)="testNotification()">
            <mat-icon>notification_add</mat-icon>
            測試通知
          </button>
        }
      </mat-card-actions>
    </mat-card>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .pwa-manager-card {
      max-width: 600px;
      margin: 20px auto;
    }

    .status-section {
      margin: 16px 0;
    }

    .status-section h3 {
      margin: 0 0 8px 0;
      font-size: 16px;
      font-weight: 500;
    }

    .cache-info {
      margin-top: 8px;
    }

    .cache-details {
      margin-top: 8px;
      padding: 8px;
      background-color: #f5f5f5;
      border-radius: 4px;
    }

    .cache-item {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      border-bottom: 1px solid #e0e0e0;
    }

    .cache-item:last-child {
      border-bottom: none;
    }

    mat-card-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    mat-chip-set {
      margin: 8px 0;
    }

    mat-chip mat-icon {
      margin-right: 4px;
    }
  `]
})
export class PwaManagerComponent implements OnInit, OnDestroy {
  private pwaService = inject(PwaService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  private destroy$ = new Subject<void>();

  // Signals for reactive state
  isOnline = signal<boolean>(navigator.onLine);
  updateAvailable = signal<boolean>(false);
  installPromptAvailable = signal<boolean>(false);
  cacheSize = signal<number>(0);
  cacheStatus = signal<{ name: string; size: number }[]>([]);
  notificationPermission = signal<NotificationPermission>('default');
  isSlowNetwork = signal<boolean>(false);

  // Computed values
  isPwaMode = computed(() => this.pwaService.isPwaMode());

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.loadCacheInfo();
    this.checkNotificationPermission();
    this.checkNetworkSpeed();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeSubscriptions(): void {
    // 監聽網路狀態
    this.pwaService.isOnline$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isOnline => {
        this.isOnline.set(isOnline);
        if (isOnline) {
          this.snackBar.open('網路連線已恢復', '關閉', { duration: 3000 });
        } else {
          this.snackBar.open('網路連線中斷，應用程式將以離線模式運行', '關閉', { duration: 5000 });
        }
      });

    // 監聽更新狀態
    this.pwaService.updateAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        this.updateAvailable.set(available);
        if (available) {
          this.snackBar.open('有新版本可用', '立即更新', { duration: 10000 })
            .onAction()
            .subscribe(() => this.updateApp());
        }
      });

    // 監聽安裝提示
    this.pwaService.installPromptAvailable$
      .pipe(takeUntil(this.destroy$))
      .subscribe(available => {
        this.installPromptAvailable.set(available);
        if (available) {
          this.snackBar.open('可以安裝此應用程式到您的裝置', '安裝', { duration: 8000 })
            .onAction()
            .subscribe(() => this.installPwa());
        }
      });
  }

  private async loadCacheInfo(): Promise<void> {
    try {
      const size = await this.pwaService.getCacheSize();
      const status = await this.pwaService.getCacheStatus();

      this.cacheSize.set(size);
      this.cacheStatus.set(status);
    } catch (error) {
      console.error('Failed to load cache info:', error);
    }
  }

  private checkNotificationPermission(): void {
    if ('Notification' in window) {
      this.notificationPermission.set(Notification.permission);
    }
  }

  private checkNetworkSpeed(): void {
    this.isSlowNetwork.set(this.pwaService.isSlowNetwork());
  }

  async installPwa(): Promise<void> {
    try {
      const installed = await this.pwaService.showInstallPrompt();
      if (installed) {
        this.snackBar.open('應用程式安裝成功！', '關閉', { duration: 3000 });
      }
    } catch (error) {
      console.error('Failed to install PWA:', error);
      this.snackBar.open('安裝失敗，請稍後再試', '關閉', { duration: 3000 });
    }
  }

  async updateApp(): Promise<void> {
    try {
      await this.pwaService.applyUpdate();
    } catch (error) {
      console.error('Failed to update app:', error);
      this.snackBar.open('更新失敗，請重新整理頁面', '關閉', { duration: 3000 });
    }
  }

  async requestNotificationPermission(): Promise<void> {
    try {
      const permission = await this.pwaService.requestNotificationPermission();
      this.notificationPermission.set(permission);

      if (permission === 'granted') {
        this.snackBar.open('通知權限已啟用', '關閉', { duration: 3000 });
      } else {
        this.snackBar.open('通知權限被拒絕', '關閉', { duration: 3000 });
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
    }
  }

  async clearCache(): Promise<void> {
    try {
      await this.pwaService.clearCache();
      await this.loadCacheInfo();
      this.snackBar.open('快取已清除', '關閉', { duration: 3000 });
    } catch (error) {
      console.error('Failed to clear cache:', error);
      this.snackBar.open('清除快取失敗', '關閉', { duration: 3000 });
    }
  }

  async testNotification(): Promise<void> {
    try {
      const notificationOptions: CustomNotificationOptions = {
        title: '測試通知',
        body: '這是一個測試通知，用來確認通知功能正常運作。',
        icon: '/icons/icon-192x192.png',
        tag: 'test-notification',
        requireInteraction: false
      };

      await this.pwaService.showNotification(notificationOptions);
    } catch (error) {
      console.error('Failed to show test notification:', error);
      this.snackBar.open('顯示通知失敗', '關閉', { duration: 3000 });
    }
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  getNotificationChipColor(): string {
    switch (this.notificationPermission()) {
      case 'granted': return 'primary';
      case 'denied': return 'warn';
      default: return 'basic';
    }
  }

  getNotificationIcon(): string {
    switch (this.notificationPermission()) {
      case 'granted': return 'notifications_active';
      case 'denied': return 'notifications_off';
      default: return 'notifications_none';
    }
  }

  getNotificationText(): string {
    switch (this.notificationPermission()) {
      case 'granted': return '已啟用';
      case 'denied': return '已拒絕';
      default: return '未設定';
    }
  }
}
